import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from '@mui/material/Tooltip';
import {
  CardMedia,
  Skeleton,
  Stack,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import { isMobile } from "react-device-detect";
import moment from "moment";
import { useNavigate, Link } from "react-router-dom";
import { PAGE_ROUTES } from "../../utils/constants";
import { VideoResponse } from "../../types/medial";
import VideoThumbnail from "../../assets/thumbnail.png";
import ShareIcon from "@mui/icons-material/Share";
import { RWebShare } from "react-web-share";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import {
  getVideoDetailsByID,
  reportVideo,
  deleteVideo,
  updateVideoDetail,
  getFeedbackStatus,
  getAd,
} from "../../services/APIServices/videos.api";
import { getChannelByID } from "../../services/APIServices/channel.api";
import { useAppSelector } from "../../store/hooks";
import { getUser } from "../../store/reducers/user.reducer";
import { isUserChannel } from "../../utils";
import { ChannelDetails } from "../../types/channel";
import { useAuth } from "../../context/AuthContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadVideo from "../UploadVideo/UploadVideo";
import { error } from "ajv/dist/vocabularies/applicator/dependencies";
import logoOnly from "../../assets/IndeeFlix-logo-only.png";
import { useTranslation } from "react-i18next";
import AdBanner from "../AdBanner/AdBanner";
import { Block, ReportGmailerrorred, WarningAmber } from "@mui/icons-material";
import { blockChannel, blockVideo } from "../../services/APIServices/block.api";
import { toast } from "react-toastify";

interface VideoData extends VideoResponse {
  src?: string;
  title: string;
  channelName: string;
  creationTime: string;
  views: number;
}

interface MediaProps {
  small?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  data: VideoData[];
}

function truncateText(title: string, maxLength: number) {
  if (title) {
    if (title.length <= maxLength) {
      return title;
    }
    const words = title.split(" ");
    const truncatedWords = words.slice(0, maxLength);
    return truncatedWords.join(" ") + "...";
  }
  return "";
}

const getDefaultGrid = (max: number | undefined) =>
  max ? { xs: max } : { lg: 4, md: 4, sm: 4, xs: 6 };

export default function VideosCard(props: any) {
  const {
    small = false,
    fullWidth = false,
    isLoading = false,
    data = [],
    maxWidth,
    videoDetails,
    setVideoDetails,
    showAd = false,
    adData = {}
  } = props;
  const [videos, setVideos] = useState<VideoData[]>(data !== null ? data : []);
  const user = useAppSelector((state) => getUser(state));
  const [loading, setLoading] = useState(isLoading);
  const [gridProps, setGridProps] = useState<any>(getDefaultGrid(maxWidth));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuID, setMenuID] = useState<null | string>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // State for delete confirmation dialog
  const [videoToDelete, setVideoToDelete] = useState<string>(""); // State to store the video ID to be deleted
  const [editVideoDetails, setEditVideoDetails] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  moment.locale(i18n.language);
  const [reported, setReported] = useState(false);
  const { verifyAuth, setOnLoginSuccess } = useAuth();
  const [isSpamDialogOpen, setIsSpamDialogOpen] = useState(false);
  const [spamMessage, setSpamMessage] = useState("");
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");


  const width = "100%";
  const height = small ? 350 : 350;

  const fetchVideoDetails = (videoID: any) => {
    if (user?.userID) {
      try {
        getFeedbackStatus(user.userID, videoID).then(
          (feedbackStatus: any) => {
            setReported(feedbackStatus?.spam);
          }
        );
      } catch (error) {
        console.error("Error fetching video details or feedback status:", error);
      }
    }
  };

  const handleReport = (video: any) => {
    setSelectedReason("");
    setCustomReason("");
    setIsReportDialogOpen(true);
  };

  const handleReportSubmit = (video: any) => {
    try {
      if (!selectedReason) {
        toast.warning("Please select a reason before submitting.");
        return;
      }

      const reason =
        selectedReason === "Other" && customReason.trim()
          ? `Other: ${customReason.trim()}`
          : selectedReason;

      const supportEmail = "contact@indeeclips.com";
      const subject = "Video Report";
      const body = [
        "Hello IndeeClips Team,",
        "",
        "I found an issue with the following video on the platform:",
        "",
        `Video Title: ${video?.title || "N/A"}`,
        `Channel Name: ${video?.channelName || "N/A"}`,
        "",
        `Reported Reason: ${reason}`,
        "",
        "Kindly look into this matter at the earliest and take the necessary action.",
        "",
        `Thanks & Regards,`,
        `${user?.email || "IndeeClips User"}`
      ].join("\n");

      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);

      const isMobile = /Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent || ""
      );

      if (isMobile) {
        window.location.href = `mailto:${supportEmail}?subject=${encodedSubject}&body=${encodedBody}`;
      } else {
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${supportEmail}&su=${encodedSubject}&body=${encodedBody}`;
        window.open(gmailUrl, "_blank", "noopener,noreferrer");
      }

      setIsReportDialogOpen(false);
      toast.success("Redirecting to email...");
    } catch (error) {
      console.error("Error while reporting video:", error);
      toast.error("Failed to open email. Please try again.");
    }
  };

  const handleSpamClick = (videoID: any) => {
    fetchVideoDetails(videoID);
    if (verifyAuth()) {
      setIsSpamDialogOpen(true);
    } else {
      setOnLoginSuccess(() => setIsSpamDialogOpen(true));
    }
  };

  const handleEdit = (videoDetails: any) => {
    console.log("Edit video");
    setEditVideoDetails(videoDetails);
    setEditing(true);
    handleMenuClose();
    // updateVideoDetail(videoDetails).then((res: any) => {
    //   if (res.success) {
    //     console.log("Video details updated successfully:", res.message);
    //   } else {
    //     console.log("Failed to update video details:", res.message);
    //   }
    // });
  };

  const handleDelete = (videoId: string) => {
    // Open delete confirmation dialog
    setVideoToDelete(videoId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteVideo(videoToDelete).then((res: any) => {
      console.log(" deleteVideo : ", res);
      setVideos(videos.filter((video) => video.videoID !== videoToDelete));
      navigate(0);
    }, (error: any) => {
      console.log("Failed to delete video:", error);
    });
    // Close delete confirmation dialog
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    if (data?.length) setVideos(data);
  }, [data]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!isMobile && fullWidth)
      setGridProps({
        lg: 8,
        md: 8,
        sm: 8,
      });
    else if (isMobile)
      setGridProps({
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      });
    else if (small && !maxWidth)
      setGridProps({
        xl: 3,
        lg: 4,
        md: 4,
        sm: 4,
        xs: 4,
      });
    else setGridProps(getDefaultGrid(maxWidth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, fullWidth, small, maxWidth]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setMenuID(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const formatNumber = (value: number | undefined, suffix: string): string => {
    if (value === undefined) return `0 ${suffix}`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B ${suffix}`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M ${suffix}`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K ${suffix}`;
    return `${value} ${suffix}`;
  };

  const handleBlockChannel = async (channelID: string) => {
    try {
      await blockChannel(channelID);
      toast.success("Channel blocked successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block channel");
    }
  };

  const handleBlockVideo = async (videoID: string) => {
    try {
      await blockVideo(videoID);

      setVideos((prevVideos) => prevVideos.filter((v) => v.videoID !== videoID));
      toast.success("Video blocked successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block video");
    }
  };

  return (
    <React.Fragment>
      <Box
        sx={{ overflow: "hidden" }}
        alignItems="center"
        justifyContent={"center"}
      >
        <Grid
          container
          alignItems="center"
          justifyContent={"left"}
        // sx={{ rowGap: "10px", columnGap: "10px" }}
        >
          {showAd && (
            < Grid item {...gridProps} sx={{ padding: 1 }}>
              <AdBanner
                width={width}
                height={height}
                fullWidth={fullWidth}
                isMobile={isMobile}
                adText={adData?.title ?? undefined}
                adImage={adData && !adData.video ? adData.url : undefined}
                adVideo={adData && adData.video ? adData.url : undefined}
                adLink={adData?.adsHomeUrl ?? undefined}
                adSponseredBy={adData?.sponsoredBy ?? undefined}
                adDescription={adData?.description ?? undefined}
                asIsSponsered={adData?.sponsored ?? false}
                adThumbnail={adData?.thumbnail ?? undefined}
              />
            </Grid>)}
          {Array.from(
            videos && videos?.length ? videos : new Array(isLoading ? 6 : 0)
          )?.map((video, index) => (
            <Grid
              item
              {...gridProps}
              key={index}
              sx={{
                padding: 1,
              }}
            >
              <Card
                key={index}
                sx={{
                  width: isMobile || fullWidth ? "100%" : width,
                  height: fullWidth ? "auto" : height,
                  position: "relative",
                  display: "flex",
                  flexDirection: fullWidth ? "row" : "column",
                  justifyContent: fullWidth ? "initial" : "space-between",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <>
                  {!loading ? (
                    <Box
                      sx={{
                        position: "relative",
                      }}
                    >
                      {/* Crawlable link for SEO */}
                      <Link
                        to={`${PAGE_ROUTES.PLAYER_PAGE}/${video?.videoID}`}
                        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                        onClick={(e) => {
                          // Prevent navigation if video isn't ready
                          if (
                            video?.videoStatus === "Processing video" ||
                            video?.videoStatus === "Video is not processed"
                          ) {
                            e.preventDefault();
                            console.warn("Video not playable yet:", video?.videoStatus);
                            return;
                          }
                        }}
                      >
                        <CardMedia
                          component="img"
                          sx={{
                            width: fullWidth ? 250 : "100%",
                            aspectRatio: "16/9",
                            objectFit:
                              video?.thumbnailPic && video.thumbnailPic.trim() !== ""
                                ? "cover"
                                : "contain",
                            backgroundColor: "#D0D4CA",
                            padding:
                              video?.thumbnailPic && video.thumbnailPic.trim() !== ""
                                ? 0
                                : "20px",
                          }}
                          alt={video?.title}
                          image={
                            video?.thumbnailPic && video.thumbnailPic.trim() !== ""
                              ? video.thumbnailPic
                              : logoOnly
                          }
                        />
                      </Link>

                      {/* Overlay only if processing */}
                      {(video?.videoStatus === "Processing video" || video?.videoStatus === "Video is not processed") && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                            backgroundColor: "rgba(0, 0, 0, 0.4)",
                            backdropFilter: "blur(1px)",
                          }}
                        >
                          {video?.videoStatus === "Processing video" ? (
                            <>
                              <CircularProgress size={24} sx={{ color: "#fff" }} />
                              <Typography variant="caption" mt={1} sx={{ color: "#fff" }}>
                                Processing video...
                              </Typography>
                            </>
                          ) : (
                            <>
                              <WarningAmber sx={{ fontSize: 28, color: "yellow" }} />
                              <Typography
                                variant="caption"
                                mt={1}
                                sx={{
                                  color: "#fff",
                                  textAlign: "center",
                                  fontWeight: 500,
                                }}
                              >
                                Video upload incomplete<br />Please re-upload
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>

                  ) : (
                    <Skeleton
                      variant="rectangular"
                      width={fullWidth ? 200 : "100%"}
                      height={fullWidth ? 152 : "50%"}
                    />
                  )}
                  <Box
                    sx={{
                      p: fullWidth ? 1 : 2,
                      marginLeft: fullWidth ? 3 : "auto",
                      width: "100%",
                    }}
                  >
                    {!loading ? (
                      <Stack direction={"column"}>
                        <Link
                          to={`${PAGE_ROUTES.PLAYER_PAGE}/${video?.videoID}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <Typography
                            gutterBottom
                            variant="h6"
                            lineHeight={fullWidth ? 2 : "auto"}
                            noWrap
                          >
                            {videos?.[index]?.title}
                          </Typography>
                        </Link>
                        <Typography
                          display="block"
                          variant="caption"
                          color="text.secondary"
                          onClick={() =>
                            navigate(
                              `${PAGE_ROUTES.CHANNEL_PAGE}/profile/${video?.channelID}`
                            )
                          }
                        >
                          {videos?.[index]?.channelName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          width={"100%"}
                        >
                          {formatNumber(video?.views, t("videoCard.views"))}
                        </Typography>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                        >
                          {videos?.[index]?.creationTime ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {`${t("subscribers.published")} • ${moment(
                                `${videos?.[index]?.creationTime
                                  ?.split("T")
                                  ?.join(" ")} UTC`
                              ).fromNow()}`}
                            </Typography>
                          ) : null}
                          <Tooltip title={t("videoCard.moreOptionsTooltip")} arrow>
                            <IconButton
                              aria-label="more"
                              aria-controls={`video-menu-${index}`}
                              aria-haspopup="true"
                              onClick={(e) =>
                                handleMenuOpen(e, `video-menu-${index}`)
                              }
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ) : (
                      <>
                        <Skeleton />
                        <Skeleton width="60%" />
                      </>
                    )}
                  </Box>
                </>
              </Card>
              <Menu
                id={`video-menu-${index}`}
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && menuID === `video-menu-${index}`}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                PaperProps={{
                  style: {
                    width: "250px",
                  },
                }}
              >
                <RWebShare
                  data={{
                    text: video?.videoID,
                    url: `${typeof window !== 'undefined' ? window.location.origin : 'https://indeeclips.com'}/watch/${video?.videoID}?type=video`,
                    title: video?.title,
                  }}
                >
                  <MenuItem>
                    <ListItemIcon>
                      <ShareIcon fontSize="small" />
                    </ListItemIcon>
                    {t("videoCard.share")}
                  </MenuItem>
                </RWebShare>

                {/* Condition to check if user uploaded the video */}
                {isUserChannel(user?.channelList, video?.channelID) ? (
                  <>
                    <MenuItem onClick={() => handleEdit(video)}>
                      <ListItemIcon>
                        <EditIcon fontSize="small" />
                      </ListItemIcon>
                      {t("videoCard.edit")}
                    </MenuItem>
                    <MenuItem onClick={() => handleDelete(video.videoID)}>
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      {t("videoCard.delete")}
                    </MenuItem>
                  </>
                ) : (
                  <>

                    <MenuItem onClick={() => handleBlockChannel(video.channelID)}>
                      <ListItemIcon>
                        <Block fontSize="small" />
                      </ListItemIcon>
                      {t("videoCard.blockChannel")}
                    </MenuItem>

                    <MenuItem onClick={() => handleBlockVideo(video.videoID)}>
                      <ListItemIcon>
                        <Block fontSize="small" />
                      </ListItemIcon>
                      {t("videoCard.blockVideo")}
                    </MenuItem>

                    <MenuItem onClick={() => handleReport(video)}>
                      <ListItemIcon>
                        <ReportGmailerrorred fontSize="small" />
                      </ListItemIcon>
                      {t("videoCard.report")}
                    </MenuItem>


                    <Divider />
                    <MenuItem onClick={() => handleSpamClick(video.videoID)}>
                      <ListItemIcon>
                        <ReportGmailerrorredIcon fontSize="small" />
                      </ListItemIcon>
                      {t("videoCard.spam")}
                    </MenuItem>
                  </>
                )}

                <Dialog open={isReportDialogOpen} onClose={() => setIsReportDialogOpen(false)}>
                  <DialogTitle>{t("reportDialog.title")}</DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t("reportDialog.description")}
                    </Typography>

                    <TextField
                      select
                      fullWidth
                      label={t("reportDialog.selectReasonLabel")}
                      value={selectedReason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      sx={{ mb: selectedReason === "Other" ? 2 : 0 }}
                    >
                      {(t("reportDialog.reasons", { returnObjects: true }) as any).map((reason: string, idx: number) => (
                        <MenuItem key={idx} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </TextField>

                    {selectedReason === "Other" && (
                      <TextField
                        fullWidth
                        label={t("reportDialog.enterReasonLabel")}
                        multiline
                        rows={3}
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                      />
                    )}
                  </DialogContent>

                  <DialogActions>
                    <Button onClick={() => setIsReportDialogOpen(false)}>
                      {t("reportDialog.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleReportSubmit(video)}
                      disabled={
                        !selectedReason ||
                        (selectedReason === "Other" && !customReason.trim())
                      }
                    >
                      {t("reportDialog.submit")}
                    </Button>
                  </DialogActions>
                </Dialog>


                <Dialog open={isSpamDialogOpen} onClose={() => setIsSpamDialogOpen(false)}>
                  <DialogTitle>{t(reported ? "removeSpam" : "reportSpam")}</DialogTitle>
                  <DialogContent>
                    {reported ? (
                      <Typography>{t("removeReportConfirmation")}</Typography>
                    ) : (
                      <TextField
                        label={t("enterReason")}
                        fullWidth
                        multiline
                        rows={4}
                        value={spamMessage}
                        onChange={(e) => setSpamMessage(e.target.value)}
                      />
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setIsSpamDialogOpen(false)}>{t("cancel")}</Button>
                    <Button
                      onClick={() => handleReport(video.videoID)}
                      color="primary"
                      disabled={!reported && !spamMessage.trim()} // Disable if no message when reporting
                    >
                      {t(reported ? "remove" : "submit")}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Menu>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deleteDialog.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("deleteDialog.cancel")}
          </Button>
          <Button onClick={confirmDelete} color="primary">
            {t("deleteDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editing}
        onClose={() => setEditing(false)}
        fullWidth
        maxWidth="md"
      >
        <UploadVideo
          isEditing
          width="100%"
          details={editVideoDetails}
          onEditSuccess={() => setEditing(false)}
          onModalClose={() => setEditing(false)}
        />
      </Dialog>

    </React.Fragment>
  );
}
